import { SubmitHandler } from "react-hook-form";
import { useBlockedTimeWizardForm } from "./useBlockedTimeWizardForm";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageMode } from "../../../../../common/types";
import { useAppContext } from "../../../../../AppContext";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import {
  getDefaultRoutePath,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IconType } from "../../../../../common/icons/Icon";
import { IBlockedTimeWizardFormModel } from "../models/blockedTimeWizardFormModel";
import { toZonedTime } from "date-fns-tz";
import { IEmployeeWithAvailabilityModel } from "../../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import {
  FULL_DAY_IN_MINUTES,
  startOfLocalDay,
} from "../utils/fullDayBlockedTimeUtility";

interface IUseBlockedTimeWizardPageProps {
  pageMode: PageMode;
  initialData?: IEventWizardInitialData;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
}

export const useBlockedTimeWizardPage = ({
  pageMode,
  initialData,
  studioEmployees,
}: IUseBlockedTimeWizardPageProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { day, month, year } = useParams();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { currentUser } = useAppContext();
  const { mutate } = useApiMutation();

  useEffect(() => {
    if (year && month && day) {
      const date = new Date(+year, +month - 1, +day, 11);
      setSelectedDate(date);
    }
  }, [year, month, day]);

  const { form, breakTypes, isBreakTypesLoading } = useBlockedTimeWizardForm(
    selectedDate,
    studioEmployees,
    initialData,
    pageMode
  );

  // const deleteBlockedTimeConfirm = () => {
  //   showCustomConfirmDialog(
  //     "Наистина ли искате да изтриете това блокирано време?",
  //     deleteBlockedTime,
  //     () => {},
  //     "warning",
  //     "Да",
  //     "Не"
  //   );
  // };

  // const deleteBlockedTime = () => {
  //   mutate({
  //     endpoint: endpointsMetadata.planner.deleteEvent,
  //     method: "delete",
  //     invalidateKeys: [[endpointsMetadata.planner.getAll]],
  //     additionalParams: `itemId=${id}`,
  //   });
  //   navigate(getDefaultRoutePath(currentUser));
  // };

  // useAddAdditionalLayoutButton({
  //   iconType: IconType.DeleteIcon,
  //   buttonOnClick: deleteBlockedTimeConfirm,
  //   validator: id !== undefined,
  // });

  const successMessage = `Блокираното време беше ${
    id ? "запазено" : "добавено"
  } успешно.`;

  const pageTitle = `${
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране"
  } на блокирано време`;

  const buttonTitle =
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране";

  const onTrySave: SubmitHandler<IBlockedTimeWizardFormModel> = async (
    data
  ) => {
    if (
      pageMode === "edit" &&
      initialData?.currentlySelectedEvent?.isRecurring
    ) {
      showCustomConfirmDialog(
        "Искате ли да приложите промяната за всички бъдещи блокирани времена от поредицата? Имайте предвид, че всички времена, които са променени, няма да бъдат засегнати.",
        () => onSave(data, true),
        () => onSave(data, false),
        "warning",
        "Да",
        "Не"
      );
    } else {
      onSave(data, false);
    }
  };

  const onSave = async (
    data: IBlockedTimeWizardFormModel,
    applyToFuture: boolean
  ) => {
    if (applyToFuture) {
      data.applyChangeToAllFutureEvents = true;
    }

    if (data.isFullDay && data.date) {
      data.date = startOfLocalDay(data.date);
      data.endDate = data.endDate ? startOfLocalDay(data.endDate) : data.date;
      data.durationInMinutes = FULL_DAY_IN_MINUTES;
      data.isRecurring = false;
    } else {
      data.endDate = undefined;
    }

    const toStudioWallClock = (value: Date) => {
      if (!initialData?.studioSettings) {
        return value;
      }

      const offsetMinutes = toZonedTime(
        value,
        initialData.studioSettings.timezone
      ).getTimezoneOffset();

      // Adjust manually
      return new Date(value.getTime() - offsetMinutes * 60 * 1000);
    };

    if (data.date) {
      data.date = toStudioWallClock(data.date);
    }

    if (data.endDate) {
      data.endDate = toStudioWallClock(data.endDate);
    }

    mutate(
      {
        endpoint: endpointsMetadata.planner.blockedTimeWizard,
        globalIsLoading: true,
        method: "post",
        invalidateKeys: [
          [endpointsMetadata.planner.getAll],
          [endpointsMetadata.planner.getEvent],
        ],
        successMessage: successMessage,
        ...data,
      },
      {
        onSuccess: () => {
          if (applyToFuture) {
            navigate(routePaths.calendar);
            return;
          }
          navigate(routePaths.calendar);
        },
      }
    );
  };

  const date = form.watch("date");
  const endDate = form.watch("endDate");
  const isFullDay = form.watch("isFullDay");

  const dateText = useMemo(() => {
    const format = (value: Date) =>
      `${String(value.getDate()).padStart(2, "0")}/${String(
        value.getMonth() + 1
      ).padStart(2, "0")}/${value.getFullYear()}`;

    if (!date) {
      return false;
    }

    const showRange =
      isFullDay && !!endDate && format(endDate) !== format(date);

    return `Дата: ${format(date)}${showRange ? ` - ${format(endDate)}` : ""}`;
  }, [date, endDate, isFullDay]);

  const onFullDayChange = (checked: boolean) => {
    if (checked) {
      const firstDay = startOfLocalDay(
        form.getValues("date") ?? selectedDate ?? new Date()
      );
      form.setValue("date", firstDay);
      form.setValue("endDate", firstDay);
      form.setValue("isRecurring", false);
    } else {
      // Template mode picks the start time again in the employee calendar.
      form.setValue("date", undefined);
      form.setValue("endDate", undefined);
    }
  };

  return {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    breakTypes,
    isBreakTypesLoading,
    isFullDay,
    onFullDayChange,
    onTrySave,
  };
};
